import React, { useState, useEffect, useContext, useCallback } from "react";
import PropTypes from "prop-types";

import Textfield from "@atlaskit/textfield";
import Spinner from "@atlaskit/spinner";
import { AsyncSelect } from "@atlaskit/select";
import SearchIcon from "@atlaskit/icon/glyph/editor/search";
import CheckIcon from "@atlaskit/icon/glyph/check";

import UploadSection from "../../UploadSection";
import { BackgroundDialogContext } from "../Context";
import { getPages, searchPages, getPageAttachments } from "../RestService";
import {
  Label,
  ImageThumbnail,
  ImagesContainer,
  ScrollContainer,
} from "../styled/StyledComponents";
import { IMAGE_TYPES } from "../ImageSourceTypes";
import SimpleBar from "../../Simplebar";
import NoResults from "../NoResults";

import { TextFieldContainer, SelectContainer } from "./Unsplash";

const OptionLabel = ({ label }) => (
  <div
    style={{
      fontSize: "14px",
      whiteSpace: "nowrap",
      textOverflow: "ellipsis",
      maxWidth: "156px",
      overflow: "hidden",
    }}
  >
    {label}
  </div>
);

function Attachments({ showCurrentPageInAttachments, showVideoInAttachments }) {
  const [isLoading, setIsLoading] = useState(false);
  const [loadingAttachments, setLoadingAttachments] = useState(false);
  const [input, setInput] = useState("");
  const [page, setPage] = useState({});
  const [macroLocation, setMacroLocation] = useState();
  const [availablePages, setAvailablePages] = useState([]);
  const [availableAttachments, setAvailableAttachments] = useState([]);

  const { image, setImage, isMulti, contentType } = useContext(
    BackgroundDialogContext
  );

  const images = availableAttachments.filter((img) =>
    img.title.toLowerCase().includes(input.toLowerCase())
  );

  const createOptions = useCallback(
    (unstyledPages) =>
      unstyledPages.map((p) => ({
        value: p.value,
        label: <OptionLabel label={p.label} />,
      })),
    []
  );

  const fetchAttachments = async () => {
    if (page?.value) {
      setLoadingAttachments(true);
      try {
        const pageAttachments = await getPageAttachments(
          page.value,
          contentType
        );
        if (!pageAttachments.length) {
          setAvailableAttachments([]);
        } else {
          const formattedAttachments = pageAttachments
            .map((att) => ({
              id: att.fileId,
              title: att.title,
              type: att.mediaType,
              size:
                Math.round((att.fileSize / 1048576 + Number.EPSILON) * 100) /
                100,
              url: `${window.AP._hostOrigin}/wiki${att.downloadLink}`,
              isVideo: att.mediaType === "video/mp4",
            }))
            .filter((att) => {
              if (showVideoInAttachments) return true;
              return !att.isVideo;
            });
          setAvailableAttachments([...formattedAttachments]);
        }
      } catch (err) {
        setIsLoading(false);
        setAvailableAttachments([]);
      } finally {
        setLoadingAttachments(false);
      }
    }
  };

  useEffect(() => {
    fetchAttachments();
  }, [page]);

  useEffect(() => {
    (async () => {
      setIsLoading(true);

      window.AP.navigator.getLocation(async (location) => {
        const { contentId } = location.context;
        const currentPage = { value: contentId, label: "Current page" };
        const [pageForSelect] = createOptions([currentPage]);

        setMacroLocation(location);
        setPage(
          showCurrentPageInAttachments ? { ...pageForSelect } : undefined
        );

        let pages = await getPages();
        pages = pages.filter((opt) => opt.value !== contentId);

        const pagesForSelect = createOptions(
          showCurrentPageInAttachments ? [currentPage, ...pages] : [...pages]
        );

        setAvailablePages([...pagesForSelect]);
        setIsLoading(false);
      });
    })();
  }, []);

  const handlePageSearch = async (e) => {
    const pages = await searchPages(e);
    const pagesForSelect = createOptions(pages);
    return pagesForSelect;
  };

  if (isLoading) {
    return (
      <div
        style={{ marginTop: "125px", display: "grid", placeContent: "center" }}
      >
        <Spinner size="large" />
      </div>
    );
  }

  return (
    <>
      <div style={{ margin: "-30px 0 10px" }}>
        <UploadSection
          selectedPage={page?.value}
          isDraft={macroLocation?.target !== "contentedit"}
          loadAttachments={fetchAttachments}
          isDisabled={!page?.value}
          setIsLoading={setLoadingAttachments}
          allowedFormats={["image/png", "image/jpeg"]}
        />
      </div>

      <SelectContainer>
        <AsyncSelect
          cacheOptions
          placeholder="Select a page"
          defaultValue={page}
          defaultOptions={availablePages}
          loadOptions={handlePageSearch}
          onChange={setPage}
          classNamePrefix="page-select"
        />
      </SelectContainer>
      <TextFieldContainer style={{ margin: "16px 0 25px" }}>
        <Textfield
          elemAfterInput={<SearchIcon size="large" />}
          placeholder="Search by image name"
          onChange={(e) => setInput(e.target.value)}
          value={input}
        />
      </TextFieldContainer>

      <ScrollContainer>
        <SimpleBar
          style={{
            position: "absolute",
            width: "400px",
            height: "calc(100% - 330px)",
            paddingRight: "15px",
          }}
        >
          {loadingAttachments ? (
            <div style={{ display: "flex", justifyContent: "center" }}>
              <Spinner size="large" />
            </div>
          ) : (
            <>
              {images.length ? (
                <>
                  <Label>{input ? "Search results" : "All"}</Label>
                  <ImagesContainer>
                    {images.map((img) => (
                      <ImageThumbnail
                        key={img.id}
                        isSelected={
                          isMulti
                            ? image.find(({ id }) => id === img.id)
                            : image.id === img.id
                        }
                        height="90px"
                        onClick={() => {
                          if (img.isVideo) {
                            const video = {
                              source: IMAGE_TYPES.VIDEO,
                              link: img.url,
                              id: img.id,
                            };
                            setImage(video);
                          } else if (isMulti) {
                            if (image.find(({ id }) => id === img.id)) {
                              return setImage([
                                ...image.filter(({ id }) => id !== img.id),
                              ]);
                            }

                            return setImage([
                              ...image,
                              {
                                source: IMAGE_TYPES.UNSPLASH,
                                link: img.url,
                                id: img.id,
                              },
                            ]);
                          } else {
                            setImage({
                              source: IMAGE_TYPES.ATTACHMENTS,
                              link: img.url,
                              id: img.id,
                            });
                          }
                        }}
                      >
                        <CheckIcon size="small" />

                        <img src={img.url} alt="Preview" />
                      </ImageThumbnail>
                    ))}
                  </ImagesContainer>
                </>
              ) : (
                <NoResults
                  label="No results"
                  content="No images found on this page."
                />
              )}
            </>
          )}
        </SimpleBar>
      </ScrollContainer>
    </>
  );
}

OptionLabel.propTypes = {
  label: PropTypes.string.isRequired,
};

export default Attachments;
